import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { PageContainer } from '@/components/shared/PageContainer';
import { ensurePermissions } from '@/hooks/usePermissions';
import { ActivityFeedNotificationsPanel } from '../-components/ActivityFeedNotificationsPanel';
import { ActivityFeedSearch } from '../-components/ActivityFeedSearch';
import { ActivityFeedTasksPanel } from '../-components/ActivityFeedTasksPanel';
import { useActivityFeed } from '../-hooks/useActivityFeed';

export const Route = createFileRoute('/(client)/activity-feed/(pages)/')({
  // Both panels read `GET /api/notifications`, which requires `network:read`
  // regardless of tab (tasks or notifications) — gate the whole page rather
  // than leave it to load into two feeds that can never fetch.
  beforeLoad: async () => {
    const perms = await ensurePermissions();
    if (!perms.network.canRead) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: ActivityFeedPage,
});

function ActivityFeedPage() {
  const navigate = useNavigate();
  const feed = useActivityFeed();

  return (
    <PageContainer className='gap-4'>
      <div className='shrink-0 flex flex-wrap items-start justify-between gap-3'>
        <h1 className='text-xl min-[1600px]:text-2xl font-bold tracking-tight text-foreground'>
          {feed.t('title')}
        </h1>

        <ActivityFeedSearch
          value={feed.companySearchInput}
          onChange={feed.onCompanySearchChange}
        />
      </div>

      <div className='grid grid-cols-1 @4xl:grid-cols-3 gap-4 flex-1 min-h-0'>
        <div className='@4xl:col-span-2 min-h-0 h-full'>
          <ActivityFeedTasksPanel
            groups={feed.groupedTasks}
            total={feed.tasksTotal}
            subTab={feed.subTab}
            onSubTabChange={feed.onSubTabChange}
            resourceType={feed.resourceType}
            onSelectResourceType={feed.onSelectResourceType}
            onClearResourceType={feed.onClearResourceType}
            isLoading={feed.isTasksLoading}
            isFetchingNextPage={feed.isTasksFetchingNextPage}
            bottomRef={feed.tasksBottomRef}
            onDismiss={feed.onDismiss}
            onReactivate={feed.onReactivate}
          />
        </div>
        <div className='min-h-0 h-full'>
          <ActivityFeedNotificationsPanel
            groups={feed.groupedNotifications}
            isLoading={feed.isNotificationsLoading}
            isFetchingNextPage={feed.isNotificationsFetchingNextPage}
            isMarkAllPending={feed.isMarkAllPending}
            bottomRef={feed.notificationsBottomRef}
            onMarkAllRead={feed.onMarkAllRead}
            onMarkRead={feed.onMarkRead}
            onOpenSettings={() => navigate({ to: '/profile/settings' })}
          />
        </div>
      </div>
    </PageContainer>
  );
}
